import React, { useEffect } from 'react';
import { Users, Shield, Zap, Cpu, CheckCircle, Target, Rocket, MessageSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

export default function WorkdayServices() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background text-text relative">
      <div className="bubble"></div>
      <div className="bubble"></div>
      <div className="bubble"></div>
      <Navbar />
      
      <div className="max-w-6xl mx-auto px-4 py-32">
        {/* Hero Section - Matching Home Page Style */}
        <section className="min-h-screen flex items-center justify-center relative overflow-hidden nebula-bg mb-20">
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/50 z-0" />
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center">
              <h1 className="text-4xl sm:text-6xl font-bold mb-6 text-glow">
                <span className="block text-reveal delay-200">
                  <span className="inline-block">Workday</span>{' '}
                  <span className="inline-block">Development</span>
                </span>
                <span className="block text-reveal delay-400">Services</span>
              </h1>
              <p className="text-xl sm:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto text-reveal delay-600">
                Ultimate Productivity Partner for Hiring Top Workday UI Testers and SMEs, through PerplexityATS. The Workday Application Tracking Platform.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center text-reveal delay-800">
                <button
                  onClick={() => navigate('/pricing')}
                  className="gradient-border bg-black/50 backdrop-blur-sm text-white px-8 py-4 rounded-lg inline-flex items-center hover:bg-black/70 transition-all transform hover:scale-105 relative group"
                >
                  For Workday Customers
                  <Users className="ml-2" />
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                    Request account access and post your Workday project requirements, connect with and hire expert consultants.
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-3 border-r-3 border-t-3 border-transparent border-t-gray-900"></div>
                  </div>
                </button>
                <button
                  onClick={() => navigate('/customer-signup')}
                  className="gradient-border bg-black/50 backdrop-blur-sm text-white px-8 py-4 rounded-lg inline-flex items-center hover:bg-black/70 transition-all transform hover:scale-105 relative group"
                >
                  For Workday Job Seekers
                  <MessageSquare className="ml-2" />
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                    Sign up today to be part of our exclusive database for upcoming and ongoing Workday projects.
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-3 border-r-3 border-t-3 border-transparent border-t-gray-900"></div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action Section */}
        <section className="mb-20">
          <h2 className="text-4xl font-bold mb-12 text-center heading-gradient">Choose Your Path - Sign Up Free Today!</h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* For Workday Services Clients */}
            <div className="card-gradient rounded-2xl p-10 glow-effect border-2 border-primary/30 hover:border-primary/60 transition-all transform hover:scale-105">
              <div className="text-center mb-8">
                <div className="inline-flex p-4 rounded-full bg-[rgba(64,224,208,0.2)] glow-effect mb-4">
                  <Users className="w-12 h-12 text-primary" />
                </div>
                <p className="text-sm font-semibold tracking-widest text-primary uppercase mb-2">PerplexityATS</p>
                <h3 className="text-3xl font-bold gradient-text mb-3">For Workday Customers</h3>
                <p className="text-xl text-gray-300 mb-6">
                  Built-into PerplexityATS — Perplexity's contracting business processes that simplify contracting and direct-hire placement via Perplexity Workday Development Services.
                </p>
                <div className="inline-block bg-primary/20 text-primary px-6 py-2 rounded-full text-lg font-semibold mb-6">
                  100% FREE TO SIGN UP
                </div>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="text-white font-semibold text-lg">Post Unlimited Job Openings</h4>
                    <p className="text-gray-400 text-sm">Share your Workday positions and project requirements with our expert network</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="text-white font-semibold text-lg">Access Verified Workday Consultants</h4>
                    <p className="text-gray-400 text-sm">Connect with pre-screened SMEs across all Workday modules</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="text-white font-semibold text-lg">Review Consultant Profiles & Resumes</h4>
                    <p className="text-gray-400 text-sm">Evaluate experience, certifications, and past project success</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="text-white font-semibold text-lg">Direct Candidate Communication</h4>
                    <p className="text-gray-400 text-sm">Message consultants directly through our platform</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="text-white font-semibold text-lg">Project Management Dashboard</h4>
                    <p className="text-gray-400 text-sm">Track timesheets, contracts, and project milestones in one place</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="text-white font-semibold text-lg">Dedicated Support Team</h4>
                    <p className="text-gray-400 text-sm">Get assistance finding the perfect consultant for your needs</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="text-white font-semibold text-lg">Flexible Team Management</h4>
                    <p className="text-gray-400 text-sm">Invite team members and manage permissions easily</p>
                  </div>
                </div>
              </div>

              <button
                onClick={() => navigate('/pricing')}
                className="w-full gradient-border bg-primary/10 text-white px-8 py-5 rounded-lg font-bold text-xl hover:bg-primary/20 transition-all transform hover:scale-105 glow-effect flex items-center justify-center"
              >
                Sign Up as Client - FREE
                <Rocket className="ml-3 w-6 h-6" />
              </button>
            </div>

            {/* For Workday Jobseekers & Professionals */}
            <div className="card-gradient rounded-2xl p-10 glow-effect border-2 border-primary/30 hover:border-primary/60 transition-all transform hover:scale-105">
              <div className="text-center mb-8">
                <div className="inline-flex p-4 rounded-full bg-[rgba(64,224,208,0.2)] glow-effect mb-4">
                  <Target className="w-12 h-12 text-primary" />
                </div>
                <h3 className="text-3xl font-bold gradient-text mb-3">For Workday Job Seekers &amp; Professionals</h3>
                <p className="text-xl text-gray-300 mb-6">
                  Get discovered by top companies and apply for Workday roles. Get access to Community Connect, for Workday Professionals. Post, share, connect and network with Workday Developers &amp; Professionals.
                </p>
                <div className="inline-block bg-primary/20 text-primary px-6 py-2 rounded-full text-lg font-semibold mb-6">
                  100% FREE TO SIGN UP
                </div>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="text-white font-semibold text-lg">Exclusive Job Opportunities</h4>
                    <p className="text-gray-400 text-sm">Get matched with Workday projects from leading organizations</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="text-white font-semibold text-lg">Professional Profile Showcase</h4>
                    <p className="text-gray-400 text-sm">Highlight your expertise, certifications, and Workday modules experience</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="text-white font-semibold text-lg">Get Notified of Interview Requests</h4>
                    <p className="text-gray-400 text-sm">Get notified if you were selected for an interview from our client pool.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="text-white font-semibold text-lg">Job Alerts & Notifications</h4>
                    <p className="text-gray-400 text-sm">Receive instant notifications for projects matching your skills</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="text-white font-semibold text-lg">Easy Application Process</h4>
                    <p className="text-gray-400 text-sm">Apply to multiple opportunities with one-click submissions</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="text-white font-semibold text-lg">Timesheet Management</h4>
                    <p className="text-gray-400 text-sm">Track hours and submit timesheets through our intuitive dashboard</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="text-white font-semibold text-lg">Community Network Access</h4>
                    <p className="text-gray-400 text-sm">Connect with other Workday professionals and share insights</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="text-white font-semibold text-lg">Career Resources & Support</h4>
                    <p className="text-gray-400 text-sm">Access training materials and career advancement resources</p>
                  </div>
                </div>
              </div>

              <button
                onClick={() => navigate('/customer-signup')}
                className="w-full gradient-border bg-primary/10 text-white px-8 py-5 rounded-lg font-bold text-xl hover:bg-primary/20 transition-all transform hover:scale-105 glow-effect flex items-center justify-center"
              >
                Sign Up - 100% FREE
                <Rocket className="ml-3 w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Additional Benefits Section */}
          <div className="mt-16 card-gradient rounded-xl p-10 glow-effect text-center">
            <h2 className="text-3xl font-bold mb-6 heading-gradient">Why Choose Perplexity for Your Workday Needs?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
              <div>
                <div className="flex items-center mb-3">
                  <Zap className="w-6 h-6 text-primary mr-2" />
                  <h4 className="text-xl font-semibold text-white">Dedicated Support for Candidate Communication</h4>
                </div>
                <p className="text-gray-300">Message Perplexity Team directly through our platform and request candidate Interview.</p>
              </div>
              <div>
                <div className="flex items-center mb-3">
                  <Shield className="w-6 h-6 text-primary mr-2" />
                  <h4 className="text-xl font-semibold text-white">Verified Professionals</h4>
                </div>
                <p className="text-gray-300">Every consultant is thoroughly vetted with verified certifications and experience.</p>
              </div>
              <div>
                <div className="flex items-center mb-3">
                  <Cpu className="w-6 h-6 text-primary mr-2" />
                  <h4 className="text-xl font-semibold text-white">AI-Powered Tools</h4>
                </div>
                <p className="text-gray-300">Leverage PerplexityALM to streamline your entire Workday project lifecycle from testing to production.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Description Section */}
        <section className="text-center mb-20">
          <div className="max-w-5xl mx-auto space-y-8">
            <div className="bg-card-bg rounded-xl p-8 glow-effect">
              <p className="text-lg sm:text-xl text-gray-300 leading-relaxed">
                Perplexity offers an all-in-one solution for organizations looking to streamline their Workday implementation and testing workflows. With our expertise and advanced AI tools, we help you optimize every step of the project lifecycle—from planning to production.
              </p>
              <ul className="mt-6 space-y-3 text-lg text-gray-300">
                <li className="flex items-center">
                  <div className="w-2 h-2 rounded-full bg-primary mr-4"></div>
                  Hire Workday UI Testing SME's
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 rounded-full bg-primary mr-4"></div>
                  Hire Workday SME Consultants
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 rounded-full bg-primary mr-4"></div>
                  Post your available Workday Positions
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 rounded-full bg-primary mr-4"></div>
                  Get Access to PerplexityALM when you hire or contract one of our SME's
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="mb-20">
          <h2 className="text-3xl font-bold mb-12 text-center heading-gradient">Our Services Include:</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {/* Workday UI Testing SME's */}
            <div className="card-gradient rounded-xl p-8 glow-effect">
              <div className="flex items-center mb-4">
                <div className="p-3 rounded-full bg-[rgba(64,224,208,0.1)] glow-effect mr-4">
                  <Users className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-2xl font-bold gradient-text">Workday UI Testing SME's</h3>
              </div>
              <p className="text-gray-300">
                Ensure the highest level of quality with our expert team of Workday UI Testing Subject Matter Experts (SMEs). They'll ensure your Workday system is user-friendly, efficient, and aligned with best practices, providing smooth, intuitive interfaces for your teams.
              </p>
            </div>

            {/* Workday SME Consultants */}
            <div className="card-gradient rounded-xl p-8 glow-effect">
              <div className="flex items-center mb-4">
                <div className="p-3 rounded-full bg-[rgba(64,224,208,0.1)] glow-effect mr-4">
                  <Shield className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-2xl font-bold gradient-text">Workday SME Consultants</h3>
              </div>
              <p className="text-gray-300">
                Our Workday SME Consultants are here to guide your organization with specialized insights and support, whether you are implementing new Workday features or enhancing your existing Workday solutions. We ensure seamless integration and optimization for maximum impact.
              </p>
            </div>

            {/* AI-Powered Efficiency */}
            <div className="card-gradient rounded-xl p-8 glow-effect">
              <div className="flex items-center mb-4">
                <div className="p-3 rounded-full bg-[rgba(64,224,208,0.1)] glow-effect mr-4">
                  <Zap className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-2xl font-bold gradient-text">AI-Powered Efficiency</h3>
              </div>
              <p className="text-gray-300">
                By leveraging Perplexity's Workday consultants and PerplexityALM, you can accelerate your development and testing workflows. Using machine learning and automation, the platform helps you detect issues early and provides better tracking of consultants' time spent on Workday tickets, enhancements, and configuration projects.
              </p>
            </div>

            {/* PerplexityALM */}
            <div className="card-gradient rounded-xl p-8 glow-effect">
              <div className="flex items-center mb-4">
                <div className="p-3 rounded-full bg-[rgba(64,224,208,0.1)] glow-effect mr-4">
                  <Cpu className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-2xl font-bold gradient-text">PerplexityALM: Revolutionizing Your Testing Workflow</h3>
              </div>
              <p className="text-gray-300">
                Gain exclusive access to PerplexityALM, our Application Life-Cycle Management software. This cutting-edge platform empowers you to transform your testing processes before any project is pushed to production. With PerplexityALM, you'll streamline your testing phase, manage releases with greater precision, and ensure faster, smoother project rollouts.
              </p>
            </div>
          </div>
        </section>

        {/* Why Choose Perplexity Section */}
        <section className="mb-20">
          <h2 className="text-3xl font-bold mb-12 text-center heading-gradient">Why Choose Perplexity?</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start space-x-4 p-6 card-gradient rounded-lg">
              <div className="p-2 rounded-full bg-[rgba(64,224,208,0.1)] glow-effect">
                <CheckCircle className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">Expert Consultation</h3>
                <p className="text-gray-300">Work with top-tier consultants who understand Workday inside and out, ensuring your project is in expert hands.</p>
              </div>
            </div>

            <div className="flex items-start space-x-4 p-6 card-gradient rounded-lg">
              <div className="p-2 rounded-full bg-[rgba(64,224,208,0.1)] glow-effect">
                <Target className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">Agile Ready</h3>
                <p className="text-gray-300">Moving to an agile environment? Perplexity specializes in agile transformations, ensuring your Workday implementation or enhancement fits seamlessly into an agile framework.</p>
              </div>
            </div>

            <div className="flex items-start space-x-4 p-6 card-gradient rounded-lg">
              <div className="p-2 rounded-full bg-[rgba(64,224,208,0.1)] glow-effect">
                <Rocket className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">AI-Driven Solutions</h3>
                <p className="text-gray-300">Save time and reduce errors with PerplexityALM designed to accelerate testing and enhance project efficiency with AI.</p>
              </div>
            </div>

            <div className="flex items-start space-x-4 p-6 card-gradient rounded-lg">
              <div className="p-2 rounded-full bg-[rgba(64,224,208,0.1)] glow-effect">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">Seamless Collaboration</h3>
                <p className="text-gray-300">Experience a true partnership where we work side by side with your team, providing constant support and driving results.</p>
              </div>
            </div>
          </div>
        </section>

      </div>

      <Footer />
    </div>
  );
}